import {CampaignType} from 'common/src/modules/campaigns/campaigns.types';
import {creativesApi} from 'common/src/modules/creatives/creatives.api';
import {TemplateType} from 'common/src/modules/creativeTemplates/creativeTemplates.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../campaignSetupAgent.enums';

// Tool schema - no parameters needed since we're fetching all creatives
const getCreativesToolSchema = z.object({
  creativeSpecs: z
    .string()
    .describe('Detailed description of the creative to get, following the user prompt.'),
});
const MAX_CREATIVES_COUNT = 10;

function getCreativesTool(store: CLStore) {
  async function execute({
    input: {creativeSpecs},
  }: ToolExecuteParams<typeof getCreativesToolSchema>): Promise<ToolResult> {
    try {
      const campaign = store.getState().campaignPage.campaignTree.campaign;

      if (!campaign) {
        return toolResult('No campaign found.');
      }

      //Get Template Type for the campaign
      const templateTypes =
        campaign.type === CampaignType.VideoAds
          ? [TemplateType.VideoAds]
          : [
              TemplateType.InteractiveVideoAds,
              TemplateType.ExternalAds,
              TemplateType.InBannerVideoAds,
              TemplateType.DisplayAds,
              TemplateType.ImageAds,
            ];

      const creativesResponse = await creativesApi.searchCreatives({
        prompt: creativeSpecs,
        templateTypes,
      });

      if (!creativesResponse.creatives.length) {
        return toolResult(`No creatives found. Reason: ${creativesResponse.filtersExplanation}`);
      }

      // Limit to maximum creatives
      const creatives = creativesResponse.creatives
        .slice(0, MAX_CREATIVES_COUNT)
        .map((creative) => ({
          id: creative.id,
          name: creative.name,
        }));

      const totalCount = creativesResponse.creatives.length;
      const isLimited = totalCount > MAX_CREATIVES_COUNT;

      const result = {
        creatives,
        totalCount,
        displayedCount: creatives.length,
        isLimited,
        instructions:
          creatives.length > 1
            ? `More than one creative found, ask the user for clarification.`
            : '',
      };

      return toolResult(JSON.stringify(result));
    } catch (error) {
      console.error('Error fetching creatives:', error);

      return toolResult(
        `Error: Failed to fetch creatives. ${
          error instanceof Error ? error.message : 'Unknown error occurred.'
        }`,
      );
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.GetCreatives,
    displayName: 'Searching creatives',
    description: "Searches for creatives from the account's creatives library based on a prompt.",
    execute,
    inputSchema: getCreativesToolSchema,
  });
}

export default getCreativesTool;
